import clsx from 'clsx';
import { Layout } from '../../components/Common/Layout';
import { Button } from 'ui/mobile-ds';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useRelationContext } from '@/context/RelationContext';
import { submitDependentQuery } from '@/queries/add-family-member.queries';
import { getLogoPath } from '@/utils/getLogoMap';
import AddFamilyMemberErrorScreen from '@/components/Common/AddFamilyMember/AddFamilyMemberErrorScreen';
import { format } from 'date-fns';
import { track } from '@/utils';
import { TRACKER_EVENTS } from '@/constants';

const ReviewDetails = () => {
  const { relationData, setRelationData, relationType } = useRelationContext();
  const [submissionError, setSubmissionError] = useState<boolean>(false); // State for submission error

  const submitDependent = submitDependentQuery();

  const {
    //  data, isError,
    isPending,
    mutate,
  } = useMutation({
    mutationFn: submitDependent.mutationFn,
    retry: false,
    onSuccess: () => {
      track({
        event: TRACKER_EVENTS.MID_TERM_ADDITION_REQUEST_COMPLETED,
        data: { relationship: relationType },
      });
      navigate('/family-addition/request-created');
    },
    onError: () => {
      setSubmissionError(true);
    },
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (!relationData) {
      navigate('/family-addition/add-family-member');
    }
  }, []);

  const handleEditDetailClick = () => {
    navigate('/family-addition/add-family-member?afmf=true');
  };
  const handleProceedClick = () => {
    //api call for submit
    mutate(relationData);
  };

  const handleCtaClick = () => {
    setRelationData({});
    navigate('/family-addition/add-family-member');
  };

  return (
    <>
      <Layout.Main
        fullWidth
        hideFooter
        rootClassName="gap-3"
        containerProps={{
          className: clsx({
            'h-full max-h-full ml-0 flex justify-center px-5 md:px-0': true,
          }),
        }}
        className="p-0"
        headerProps={{
          hidePageHeader: true,
          sticky: true,
        }}
      >
        {submissionError ? (
          <AddFamilyMemberErrorScreen
            title="Your details could not be submitted"
            subtitle="Please reach out to our support at"
            cta="Go Back"
            handleCtaClick={handleCtaClick}
            email="care@plumhq.com"
          />
        ) : (
          <div className="flex items-center justify-center flex-col gap-6">
            <img
              src={getLogoPath(
                relationData?.relationship,
                relationData?.gender
              )}
              alt={`${relationData?.relationship} ${relationData?.gender}`}
              height={100}
              width={100}
            />
            <div className="flex flex-col gap-2 items-center">
              <p className="text-2xl text-gray-600 font-black font-chromatica text-center">
                Review Dependent Details
              </p>
              <p className="text-sm font-normal text-gray-600 text-center">
                Please review the following dependant details before creating
                the request
              </p>
            </div>
            <div className="p-6 flex flex-col gap-4 items-start rounded-xl bg-[#F5F2ED] w-full">
              {/* Conditionally adjust grid columns based on the presence of dateOfMarriage */}
              <div
                className={clsx(
                  'grid gap-4',
                  'grid-cols-1', // Mobile: one field per row
                  'md:grid-cols-1', // Tablet: default to one column initially
                  relationData?.dateOfMarriage
                    ? 'lg:grid-cols-2 lg:gap-x-20'
                    : 'lg:grid-cols-3' // Large screen: 2 or 3 columns based on dateOfMarriage
                )}
              >
                {/* Family Member Name */}
                <div className="flex flex-col gap-1 items-start">
                  <p className="text-sm text-gray-400">Family Member Name</p>
                  <p className="text-base font-semibold text-gray-600 capitalize">
                    {relationData?.firstName +
                      ' ' +
                      (relationData?.lastName ? relationData?.lastName : '')}
                  </p>
                </div>

                {/* Relationship with you */}
                <div className="flex flex-col gap-1 items-start">
                  <p className="text-sm text-gray-400">Relationship with you</p>
                  <p className="text-base font-semibold text-gray-600">
                    {relationData?.relationship}
                  </p>
                </div>

                {/* Date of Birth */}
                <div className="flex flex-col gap-1 items-start">
                  <p className="text-sm text-gray-400">Date of Birth</p>
                  <p className="text-base font-semibold text-gray-600">
                    {format(relationData?.dateOfBirth, 'dd-MMM-yyyy')}
                  </p>
                </div>

                {/* Date of Marriage (conditionally displayed) */}
                {relationData?.dateOfMarriage && (
                  <div className="flex flex-col gap-1 items-start">
                    <p className="text-sm text-gray-400">Date of Marriage</p>
                    <p className="text-base font-semibold text-gray-600">
                      {format(relationData?.dateOfMarriage, 'dd-MMM-yyyy')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-6">
              <Button
                intent="tertiary"
                onClick={handleEditDetailClick}
                className="text-lg"
              >
                Edit Details
              </Button>
              <Button
                onClick={handleProceedClick}
                loading={isPending}
                className="text-lg"
              >
                Proceed
              </Button>
            </div>
          </div>
        )}
      </Layout.Main>
    </>
  );
};

export default ReviewDetails;
